import { useRef } from 'react'
import type { JSX } from 'react'
import { createPortal } from 'react-dom'

import { DndContext, DragOverlay } from '@dnd-kit/core'
import type { UniqueIdentifier } from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'

import {
  Button,
  XIcon,
  EmptyState,
  Flex,
  SearchIcon,
  SimpleSpinner,
  toast,
} from '@buffer-mono/popcorn'

import { useIdeaComposer } from '~publish/components/IdeaManagementRouter'
import {
  useHasOrganizationAction,
  usePlan,
} from '~publish/contexts/AccountContext'
import { DROP_ANIMATION } from '~publish/helpers/dndkit/constants'
import { useQueryParam } from '~publish/hooks/useQueryParam'

import { restrictToRef } from '../../../../helpers/dndkit/restrictToRefModifier'
import { useDeleteIdea } from '../../hooks/useDeleteIdea'
import { IdeaCard } from '../IdeaCard'
import { Column } from './Column'
import { CreateUploadDropzone } from '../CreateUploadDropzone'
import { useIdeasMultiSelect } from '../IdeasMultiSelect'
import { createAnnouncements } from './helpers/createAnnouncements'
import { UNASSIGNED_GROUP_ID, UNASSIGNED_GROUP_NAME } from './helpers/helpers'
import { useBoardData } from './hooks/useBoardData'
import { useCreateIdeaGroup } from './hooks/useCreateIdeaGroup'
import { useDndBoard } from './hooks/useDndBoard'
import { useMoveIdea } from './hooks/useMoveIdea'
import type { MoveIdeaInput } from './hooks/useMoveIdea'
import { useMoveIdeaGroup } from './hooks/useMoveIdeaGroup'
import { NewColumn } from './NewColumn'
import { SortableColumn } from './SortableColumn'
import SortableItem from './SortableItem'

import styles from './Board.module.css'

type BoardProps = {
  onDrop?: (files: File[], groupId?: string) => void
  onPaste?: (files: File[], groupId?: string) => void
}

const MAX_FREE_GROUPS = 3

export const Board = ({ onDrop, onPaste }: BoardProps): JSX.Element => {
  const boardRef = useRef<HTMLDivElement>(null)
  const [selectedTagIds = [], setSelectedTagIds] =
    useQueryParam<string[]>('tagIds')
  const { editIdeaWithComposer } = useIdeaComposer()
  const { data, loading, error, hasData } = useBoardData()
  const { deleteIdea } = useDeleteIdea()
  const moveIdea = useMoveIdea()
  const moveGroup = useMoveIdeaGroup()
  const createIdeaGroup = useCreateIdeaGroup()
  const { selectedIds, selectIdea, selecting } = useIdeasMultiSelect()
  const plan = usePlan()
  const canManageIdeas = useHasOrganizationAction('manageIdeas')

  const handleCreateColumn = async (name: string): Promise<void> => {
    try {
      await createIdeaGroup(name)
      toast.success(`Group ${name} created successfully`)
    } catch {
      toast.error(`Failed to create group ${name}. Please try again later.`)
    }
  }

  async function handleItemMoved({
    groupId,
    ideaId,
    placeAfterId,
  }: MoveIdeaInput): Promise<void> {
    try {
      await moveIdea({
        groupId,
        ideaId,
        placeAfterId:
          placeAfterId === UNASSIGNED_GROUP_ID ? undefined : placeAfterId,
      })
    } catch (moveIdeaError: unknown) {
      const message =
        moveIdeaError instanceof Error
          ? moveIdeaError.message
          : 'Please try again later'
      toast.error(`Failed to move idea. ${message}.`)
      throw moveIdeaError
    }
  }

  async function handleColumnMoved({
    id,
    placeAfterId,
  }: {
    id: string
    placeAfterId?: string
  }): Promise<void> {
    try {
      await moveGroup(
        id,
        placeAfterId === UNASSIGNED_GROUP_ID ? undefined : placeAfterId,
      )
    } catch (moveGroupError) {
      toast.error('Failed to move group. Please try again later.')
      throw moveGroupError
    }
  }

  const {
    activeId: activeDraggedId,
    items,
    columns,
    lockOverlayId,
    contextProps,
  } = useDndBoard(
    data.itemsMap ?? {},
    handleItemMoved,
    handleColumnMoved,
    isGroupLocked,
  )

  function isGroup(id: UniqueIdentifier): boolean {
    return data.groups.some((group) => group.id === id)
  }

  function isGroupLocked(id: UniqueIdentifier): boolean {
    return data.groups.find((group) => group.id === id)?.isLocked ?? false
  }

  function isIdea(id: UniqueIdentifier): boolean {
    return data.ideas.some((idea) => idea.id === id)
  }

  // TODO: add skeleton loading state
  if (loading && !hasData) {
    return (
      <Flex align="center" justify="center">
        <SimpleSpinner
          size="medium"
          aria-label="Loading ideas"
          // The spinner communicates indeterminate loading rather than numeric progress.
          // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="status"
        />
      </Flex>
    )
  }

  if (error) {
    return (
      <EmptyState variant="critical">
        <EmptyState.Icon>
          <XIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Error while loading ideas</EmptyState.Heading>
        <EmptyState.Description>
          Try refreshing a page, let us know if error persists
        </EmptyState.Description>
      </EmptyState>
    )
  }

  const hasVisibleIdeas = Object.values(data.itemsMap).some(
    (ideaIds) => ideaIds.length > 0,
  )

  if (selectedTagIds.length > 0 && !hasVisibleIdeas) {
    return (
      <EmptyState size="xlarge">
        <EmptyState.Icon>
          <SearchIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>No ideas match your filters</EmptyState.Heading>
        <EmptyState.Description>
          Try a different filter or clear your filters to see all ideas.
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button onClick={(): void => setSelectedTagIds([])}>
            Clear filters
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    )
  }

  const renderIdea = (id: UniqueIdentifier): JSX.Element | null => {
    const idea = data.ideas.find((candidateIdea) => candidateIdea.id === id)
    if (!idea) return null
    return (
      <IdeaCard
        idea={idea}
        titleAs="h3"
        variant="compact"
        onOpen={(openedIdea): void => {
          editIdeaWithComposer({
            source: 'create-board-ideaCard-card-1',
            ideaId: openedIdea.id,
          })
        }}
        onDelete={deleteIdea}
        selectable={selecting}
        selected={selectedIds.includes(idea.id)}
        onSelect={selectIdea}
        onMoveIntoGroup={(ideaId, groupId): Promise<void> =>
          handleItemMoved({ ideaId, groupId })
        }
      />
    )
  }

  const columnsToRender = columns.filter((id) =>
    data.groups.some((g) => g.id === id),
  )
  const sortableColumns = columnsToRender.filter((id) => !isGroupLocked(id))
  const isGroupLimitReached =
    plan.id === 'free' && columnsToRender.length >= MAX_FREE_GROUPS

  return (
    <DndContext
      autoScroll={true}
      {...contextProps}
      // This is a custom modifier to restrict the drag to the board container only
      modifiers={[restrictToRef(boardRef)]}
      accessibility={{
        announcements: createAnnouncements(data.groups, data.ideas),
      }}
    >
      <div className={styles.wrapper} ref={boardRef}>
        {/* Unassigned column */}
        <SortableColumn id={UNASSIGNED_GROUP_ID} disabled={true}>
          <CreateUploadDropzone
            onDrop={(files: File[]): void =>
              onDrop?.(files, `${UNASSIGNED_GROUP_ID}`)
            }
            onPaste={(files: File[]): void =>
              onPaste?.(files, `${UNASSIGNED_GROUP_ID}`)
            }
          >
            <Column
              id={UNASSIGNED_GROUP_ID}
              name={UNASSIGNED_GROUP_NAME}
              editable={false}
              items={items[UNASSIGNED_GROUP_ID] ?? []}
              renderItem={renderIdea}
            />
          </CreateUploadDropzone>
        </SortableColumn>
        {/* Sortable column */}
        {columnsToRender.length > 0 && (
          <SortableContext
            id="board"
            items={sortableColumns}
            strategy={horizontalListSortingStrategy}
          >
            {columnsToRender.map((id) => (
              <SortableColumn
                id={id}
                key={id}
                disabled={isGroupLocked(id) || !canManageIdeas}
              >
                <CreateUploadDropzone
                  disabled={isGroupLocked(id)}
                  onDrop={(files: File[]): void => onDrop?.(files, `${id}`)}
                  onPaste={(files: File[]): void => onPaste?.(files, `${id}`)}
                >
                  <Column
                    id={id}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    name={data.groups.find((g) => g.id === id)!.name}
                    items={items[id] ?? []}
                    renderItem={renderIdea}
                    locked={isGroupLocked(id)}
                    showLockOverlay={isGroupLocked(id) && lockOverlayId === id}
                  />
                </CreateUploadDropzone>
              </SortableColumn>
            ))}
          </SortableContext>
        )}

        {/* Create new column */}
        {canManageIdeas && (
          <NewColumn
            onCreate={handleCreateColumn}
            limitReached={isGroupLimitReached}
          />
        )}
      </div>

      {createPortal(
        <DragOverlay
          dropAnimation={DROP_ANIMATION}
          className={styles.dragOverlay}
        >
          {/* Render dragged column */}
          {activeDraggedId && isGroup(activeDraggedId) && (
            <SortableColumn id={activeDraggedId} dragOverlay={true}>
              <Column
                id={activeDraggedId}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                name={data.groups.find((g) => g.id === activeDraggedId)!.name}
                items={items[activeDraggedId]}
                onOverlay
                renderItem={renderIdea}
              />
            </SortableColumn>
          )}

          {/* Render dragged idea */}
          {activeDraggedId && isIdea(activeDraggedId) && (
            <SortableItem id={activeDraggedId} dragOverlay={true}>
              {renderIdea(activeDraggedId)}
            </SortableItem>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}
